import React, { useMemo } from 'react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import BarChart from '../../../components/visualizations/BarChart';

interface ClaimRanking {
  claim: string;
  net_score: number;
  most_count: number;
  least_count: number;
}

interface ClaimCluster {
  cluster_name: string;
  claims: { claim: string; net_score: number }[];
  insight: string;
  recommendation?: string;
}

interface ClaimsTestingData {
  task: string;
  product_name?: string;
  product_description?: string;
  category?: string;
  executive_summary?: string;
  maxdiff_rankings?: ClaimRanking[];
  visualization?: any[];
  claim_clusters?: ClaimCluster[];
  total_respondents?: number;
  total_sets?: number;
}

interface ClaimsTestingResultsProps {
  simulationResponse: string;
  contentData?: any;
}

const ClaimsTestingResults: React.FC<ClaimsTestingResultsProps> = ({
  simulationResponse,
}) => {
  const data = useMemo<ClaimsTestingData | null>(() => {
    try {
      return JSON.parse(simulationResponse);
    } catch {
      return null;
    }
  }, [simulationResponse]);

  if (!data) {
    return (
      <div className="p-6">
        <MarkdownRenderer content={simulationResponse} />
      </div>
    );
  }

  const barViz = data.visualization?.[0];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Claims Testing — {data.product_name || 'Results'}
        </h2>
        {data.category && (
          <p className="text-sm text-gray-500 mt-1">Category: {data.category}</p>
        )}
        {(data.total_respondents || data.total_sets) && (
          <p className="text-xs text-gray-400 mt-1">
            {data.total_respondents} respondents &middot; {data.total_sets} MaxDiff sets evaluated
          </p>
        )}
      </div>

      {/* Executive Summary */}
      {data.executive_summary && (
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Executive Summary</h3>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <MarkdownRenderer content={data.executive_summary} />
          </div>
        </section>
      )}

      {/* MaxDiff Chart */}
      {barViz && (
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Claims Ranking</h3>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <BarChart
              visualization={{
                type: 'bar_chart',
                title: barViz.title || 'MaxDiff Net Scores',
                data: barViz.data || [],
              }}
              questionType="max_diff"
            />
          </div>
        </section>
      )}

      {/* Rankings Table */}
      {data.maxdiff_rankings && data.maxdiff_rankings.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Detailed Rankings</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Claim
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Score
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Best
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Worst
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.maxdiff_rankings.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          i === 0
                            ? 'bg-green-100 text-green-700'
                            : i === data.maxdiff_rankings!.length - 1
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{r.claim}</td>
                    <td
                      className={`px-4 py-3 text-sm text-right font-semibold ${
                        r.net_score > 0
                          ? 'text-green-600'
                          : r.net_score < 0
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}
                    >
                      {r.net_score > 0 ? '+' : ''}
                      {r.net_score}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{r.most_count}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-600">{r.least_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </section>
      )}

      {/* Claim Clusters */}
      {data.claim_clusters && data.claim_clusters.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Claim Clusters</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {data.claim_clusters.map((cluster, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col"
              >
                <h4 className="font-semibold text-gray-900 mb-2">{cluster.cluster_name}</h4>
                <ul className="space-y-1 mb-3 flex-1">
                  {cluster.claims.map((c, ci) => (
                    <li key={ci} className="text-sm flex justify-between">
                      <span className="text-gray-700">{c.claim}</span>
                      <span
                        className={`ml-2 font-mono text-xs ${
                          c.net_score > 0
                            ? 'text-green-600'
                            : c.net_score < 0
                            ? 'text-red-600'
                            : 'text-gray-400'
                        }`}
                      >
                        {c.net_score > 0 ? '+' : ''}
                        {c.net_score}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600 border-t border-gray-100 pt-3">
                  {cluster.insight}
                </p>
                {cluster.recommendation && (
                  <p className="text-sm text-blue-700 mt-2 font-medium">
                    {cluster.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ClaimsTestingResults;
